import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { PawPrint } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { fmtErr } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created! Welcome to the pack.");
      navigate("/portal");
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="max-w-md mx-auto px-4 py-14">
      <div className="bg-white rounded-2xl border border-[#BFE8E9] p-8 shadow-sm animate-rise">
        <div className="flex items-center gap-3 mb-6">
          <img src="/logo-wide.png" alt="Pet's Pantry" className="h-14 rounded-xl" />
          <div>
            <h1 className="font-display font-bold text-xl">Join the pack</h1>
            <p className="text-xs text-slate-500">Create your pet parent account</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Full name</Label><Input required value={form.name} onChange={set("name")} data-testid="register-name" /></div>
          <div><Label>Email</Label><Input type="email" required value={form.email} onChange={set("email")} data-testid="register-email" /></div>
          <div><Label>Mobile number</Label><Input value={form.phone} onChange={set("phone")} placeholder="09xx-xxx-xxxx" data-testid="register-phone" /></div>
          <div><Label>Password</Label><Input type="password" required minLength={8} value={form.password} onChange={set("password")} data-testid="register-password" /></div>
          {error && <p className="text-sm text-red-600" data-testid="register-error">{error}</p>}
          <Button type="submit" className="w-full bg-[#E84855] hover:bg-[#C62836]" disabled={loading} data-testid="register-submit-btn">
            {loading ? "Creating…" : "Create Account"}
          </Button>
        </form>
        <div className="my-4 flex items-center gap-3 text-xs text-slate-400">
          <span className="flex-1 border-t border-[#BFE8E9]" /> or <span className="flex-1 border-t border-[#BFE8E9]" />
        </div>
        <Button type="button" variant="outline" className="w-full" onClick={googleLogin} data-testid="google-register-btn">
          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
          Continue with Google
        </Button>
        <p className="mt-4 text-sm text-center text-slate-500">
          Already have an account? <Link to="/login" className="text-[#0E7C84] hover:underline" data-testid="login-link">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
